import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from './calls_queue.module.scss';
import { Filters } from '../../../components/menus/Filters';
import Queues from './queues';
import Agents from './agents';
import { useTypedSelector } from '../../../redux/hooks';
import { selectSortedQueuePanels } from '../../../redux/slices/queueSlice';
import StyledTabs from '../../../components/StyledComponents/StyledTabs';
import { usePolyglot } from "../../../context/Polyglot";
import SettingsSlider from '../../settings/SettingsPages/SettingsComponents/SettingsSlider';
import { queueApi } from '../../../redux/services/queueApi';
import { selectCurrentUserId } from '../../../redux/slices';
import Icon from '../../../components/Icon';
import StyledSlider from '../../../components/StyledComponents/StyledSlider';
import { StyledButton } from '../../../components/StyledComponents';
import { usePanelContext } from './panelContext';
import StyledList from '../../../components/StyledComponents/StyledList';

interface FilterSelectProps {
    queues: { uuid: string; name: string }[];
    selectedIds: string[];
    valSelected: (newVals: string) => void;
    placeholder?: string;
    filterHeading?: string;
}

const QueueFilterSelect: React.FC<FilterSelectProps> = ({
    queues,
    placeholder,
    selectedIds,
    valSelected,
    filterHeading,
}) => (
    <Filters
        filter={selectedIds}
        filters={[[{ title: 'misc', value: '' }].concat(
            queues.map(queue => ({
                title: queue.name,
                value: queue.uuid,
            }))
        )]}
        onChange={valSelected}
        label="Custom Queue Filter"
        className={styles.filter}
        placeholder={placeholder}
        filterHeading={filterHeading}
    />
);

export function numSecToString(seconds: number): string {

    const hh = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const ss = String(Math.floor(seconds % 60)).padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
}

const CallQueues = () => {
    const {
        volume,
        isMuted,
        selectedTab,
        selectedPanelList,
        updatePanelState,
        includeOffline,
        agentFilterOption,
        playNotify
    } = usePanelContext();

    const panels = useTypedSelector(selectSortedQueuePanels) || [];
    const voip_user_uuid = useTypedSelector(selectCurrentUserId);

    const { isLoading: dataLoading } = queueApi.endpoints.getQueuePanelData.useQuery({ voip_user_uuid });

    const [showAllFilters, setShowAllFilters] = useState<boolean>(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showAudioOptions, setShowAudioOptions] = useState(false);

    const filterListRef = useRef<HTMLDivElement>(null);

    const { t } = usePolyglot();

    useEffect(() => {
        if (filterListRef.current) {
            const collapseHeight = 70;
            const { scrollHeight } = filterListRef.current;
            setIsCollapsed(scrollHeight > collapseHeight);
        }
    }, [selectedPanelList.length, showAllFilters]);

    useEffect(() => {
        updatePanelState({
            selectedPanelList: selectedPanelList.filter(uuid => panels.findIndex(p => p.uuid === uuid) >= 0)
        })
    }, [panels.length]);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const sortedPanels = useMemo(() => panels.toSorted((a, b) => a.name.localeCompare(b.name)), [panels.length]);

    const filteredPanels = useMemo(() => {
        if (selectedPanelList.length > 0) {
            return sortedPanels.filter(p => selectedPanelList.includes(p.uuid));
        }

        return sortedPanels;
    }, [sortedPanels.length, selectedPanelList.length]);

    const activeFilterPanels: { name: string, uuid: string }[] = useMemo(() => {
        if (selectedPanelList.length < 1) {
            return []
        }

        return filteredPanels.map(panel => ({
            name: panel.name,
            uuid: panel.uuid
        }))
    }, [filteredPanels.length, selectedPanelList.length]);

    const handlePanelFilterClick = (panelUuid: string) => {
        const currIdx = selectedPanelList.indexOf(panelUuid);

        const newPanelList = currIdx >= 0 ?
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            selectedPanelList.toSpliced(currIdx, 1)
            : selectedPanelList.concat(panelUuid);

        updatePanelState({
            selectedPanelList: newPanelList
        });
    };

    const tabOptions = [
        {
            value: 'queues',
            label: 'Queues',
            node: <span className={styles.btn_text}>Queues</span>,
        },
        {
            value: 'agents',
            label: 'Agents',
            node: <span className={styles.btn_text}>Agents</span>,
        },
    ];

    const options = [{
        label: isMuted ? 'Unmute' : 'Mute',
        value: 'mute'
    }, {
        label: 'Test Volume',
        value: 'play'
    }];

    return (
        <div className={styles.main_container}>
            <div className={styles.queue_header}>
                <div className={styles.headerBtn}>
                    <StyledTabs
                        options={tabOptions}
                        selectedTab={selectedTab}
                        setSelectedTab={(val: string) => {
                            updatePanelState({ selectedTab: val as 'queues' | 'agents' })
                        }}
                    />
                    <div className={styles.volume}>
                        <StyledButton
                            buttonStyle='text'
                            iconOnly
                            onClick={() => {
                                updatePanelState({
                                    isMuted: !isMuted
                                });
                            }}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowAudioOptions(true);
                            }}
                            size='medium'
                        >
                            <Icon name={isMuted ? 'speakerWaveUnfilled' : 'speakerWave'} />
                            {isMuted ? <i className={styles.muted_line} /> : null}
                        </StyledButton>
                        <StyledSlider
                            val={isMuted ? 0 : volume}
                            setVal={(v) => updatePanelState({
                                volume: v,
                                isMuted: false,
                            })}
                        />
                        {showAudioOptions ? (
                            <StyledList
                                options={options}
                                closeList={() => setShowAudioOptions(false)}
                                onSelect={(v) => {
                                    if (v === 'mute') {
                                        updatePanelState({
                                            isMuted: !isMuted,
                                        })
                                    } else {
                                        playNotify();
                                    }
                                }}
                            />
                        ) : null}
                    </div>
                </div>
                <div className={styles.group_container_head}>
                    <QueueFilterSelect
                        queues={sortedPanels}
                        selectedIds={selectedPanelList}
                        valSelected={handlePanelFilterClick}
                        placeholder="Select Queue"
                        filterHeading="Queue Filter"
                    />
                    {selectedTab === 'agents' ? (
                        <>
                            <QueueFilterSelect
                                queues={[
                                    { uuid: 'All Agents', name: 'All Agents' },
                                    { uuid: 'By Queue', name: 'By Queue' },
                                ]}
                                placeholder={agentFilterOption === 'By Queue' ? 'Queue' : 'All Agents'}
                                filterHeading="Group By"
                                selectedIds={[agentFilterOption]}
                                valSelected={(val) => {
                                    updatePanelState({
                                        selectedPanelUuid: undefined,
                                        agentFilterOption: val
                                    });
                                }}
                            />
                            <SettingsSlider
                                title="Include Offline"
                                checked={includeOffline}
                                onChange={() => updatePanelState({
                                    includeOffline: !includeOffline
                                })}
                            />
                        </>
                    ) : null}
                </div>
            </div>
            {activeFilterPanels.length > 0 ? (
                <div style={{ display: 'flex' }}>
                    <div className={styles.filter_container}>
                        <div className={styles.filter}>
                            <span className={styles.filter_heading}>
                                {t('terms.applied_filter', activeFilterPanels.length)}:
                            </span>
                            <div ref={filterListRef}
                                className={`${styles.filter_list} ${!showAllFilters ? styles.collapsed : ''}`}>
                                {activeFilterPanels.map(({ name, uuid }) => (
                                    <div key={`filter_panel_${uuid}`} className={styles.filter_item}>
                                        <span>{name}</span>
                                        <button
                                            className={styles.filter_close_btn}
                                            onClick={() => handlePanelFilterClick(uuid)}
                                        ><p>✕</p>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.clear_show_container}>
                        <div className={styles.clear_all_container}>
                            <div
                                className={styles.clear_all_btn}
                                onClick={() => updatePanelState({
                                    selectedPanelList: []
                                })}
                            >
                                <span className={styles.clear_icon}>✕</span>
                                <p>Clear All</p>
                            </div>
                        </div>
                        {isCollapsed && activeFilterPanels.length > 0 && (
                            <div className={styles.toggleRow}>
                                <button
                                    className={styles.toggleBtn}
                                    onClick={() => setShowAllFilters(prev => !prev)}
                                >
                                    <p>{showAllFilters ? 'Show Fewer Filters' : 'Show More Filters'}</p>
                                    <div>{showAllFilters ? <i className='icon-arrow-up' /> :
                                        <i className='icon-arrow-down' />}</div>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
            {(selectedTab === 'queues' && filteredPanels.length === 0) ? (
                <div className={styles.noDataMessage}>
                    <div>
                        <h3>No queues available</h3>
                        <h4>Please create a queue using the Queue Management panel on your dashboard.</h4>
                    </div>
                </div>
            ) : null}
            {selectedTab === 'queues' ? (
                <Queues
                    panels={filteredPanels}
                    dataLoading={dataLoading}
                />
            ) : null}
            {selectedTab === 'agents' ? (
                <Agents
                    onBack={() => {
                        updatePanelState({
                            selectedTab: 'queues',
                            selectedPanelUuid: undefined,
                        });
                    }}
                    dataLoading={dataLoading}
                />
            ) : null}
        </div >
    );
}

export default CallQueues;
